/* ==========================================================================
   TASK 260921T1C — type_collection_v2 customisable columns + persistence.

   Presentation only. The script writes a token list to data-tcv-hide on the
   table root and CSS hides the matching cells (desktop table AND the T1B
   reflow). It never touches product data, price logic, hide_price, facets,
   sort or pagination — it only changes which cells are painted.

   Storage: localStorage key rf_tcv_columns_<collection handle>, holding the
   list of HIDDEN column ids. Storing hidden (not visible) ids means a column
   added later is visible by default and old saved state stays valid. Ids that
   the current page does not render are ignored.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'rf_tcv_columns_';
  var CONTROL_SEL = '[data-tcv-columns-control]';
  var TABLE_SEL = '[data-tcv-table]';
  var BOX_SEL = 'input[data-tcv-column]';
  var ROW_SEL = 'tr.rf-tcv__row';
  var HIDE_ATTR = 'data-tcv-hide';
  var LAST_ATTR = 'data-tcv-last';
  var CHANGE_DEBOUNCE = 150;
  var SLIDER_SEL = '[data-tcv-slider]';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function storageKey(scope) {
    return STORAGE_PREFIX + (scope || 'all');
  }

  /** Saved state is untrusted input: only non-empty strings survive. */
  function readHidden(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return parsed.filter(function (id) {
        return typeof id === 'string' && id;
      });
    } catch (error) {
      return null;
    }
  }

  function writeHidden(key, ids) {
    try {
      if (ids.length) {
        window.localStorage.setItem(key, JSON.stringify(ids));
      } else {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      /* Private mode or storage disabled: the control still works this view. */
    }
  }

  function boxes(control) {
    return toArray(control.querySelectorAll(BOX_SEL));
  }

  function boxId(box) {
    return box.getAttribute('data-tcv-column');
  }

  function availableIds(control) {
    return boxes(control).map(boxId);
  }

  function tableFor(control) {
    var scope = control.closest('.rf-tcv') || document;
    return scope.querySelector(TABLE_SEL);
  }

  /** Keeps only ids this page renders, without duplicates. */
  function sanitise(ids, available) {
    return ids.filter(function (id, index) {
      return available.indexOf(id) !== -1 && ids.indexOf(id) === index;
    });
  }

  function isHiddenInList(cell, hidden) {
    return hidden.indexOf(cell.getAttribute('data-col')) !== -1;
  }

  /** Marks the last rendered spec cell of every row so no separator dangles. */
  function markLastSeparator(table, hidden) {
    toArray(table.querySelectorAll(ROW_SEL)).forEach(function (row) {
      var last = null;
      toArray(row.querySelectorAll('[data-col]')).forEach(function (cell) {
        cell.removeAttribute(LAST_ATTR);
        if (isHiddenInList(cell, hidden)) return;
        if (cell.textContent.trim() === '') return;
        last = cell;
      });
      if (last) last.setAttribute(LAST_ATTR, '');
    });
  }

  function apply(control, table, hidden) {
    var token = hidden.join(' ');
    var current = table.getAttribute(HIDE_ATTR) || '';
    var available = availableIds(control);
    var visible = available.length - sanitise(hidden, available).length;
    var label = visible + '/' + (available.length + 1);
    var countNode = control.querySelector('[data-tcv-columns-count]');

    if (current !== token) {
      table.setAttribute(HIDE_ATTR, token);
      markLastSeparator(table, hidden);
    }
    if (countNode && countNode.textContent.trim() !== label) {
      countNode.textContent = label;
    }
    boxes(control).forEach(function (box) {
      var checked = hidden.indexOf(boxId(box)) === -1;
      if (box.checked !== checked) box.checked = checked;
    });
  }

  function hiddenFromBoxes(control) {
    return boxes(control)
      .filter(function (box) {
        return !box.checked;
      })
      .map(boxId);
  }

  function sync(control) {
    var table = tableFor(control);
    if (!table) {
      control.hidden = true;
      return;
    }
    control.hidden = false;
    var available = availableIds(control);
    var stored = readHidden(storageKey(control.getAttribute('data-tcv-collection')));
    apply(control, table, stored ? sanitise(stored, available) : []);
  }

  function syncAll() {
    toArray(document.querySelectorAll(CONTROL_SEL)).forEach(sync);
    syncSliders();
  }

  function syncSlider(slider) {
    var viewport = slider.querySelector('[data-tcv-samples]');
    if (!viewport) return;

    var overflowing = viewport.scrollWidth - viewport.clientWidth > 2;
    slider.classList.toggle('is-overflowing', overflowing);

    var previous = slider.querySelector('[data-tcv-slider-prev]');
    var next = slider.querySelector('[data-tcv-slider-next]');
    var atStart = viewport.scrollLeft <= 2;
    var atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;

    if (previous) {
      previous.hidden = !overflowing || atStart;
      previous.disabled = !overflowing || atStart;
    }
    if (next) {
      next.hidden = !overflowing || atEnd;
      next.disabled = !overflowing || atEnd;
    }
  }

  function syncSliders() {
    toArray(document.querySelectorAll(SLIDER_SEL)).forEach(syncSlider);
  }

  /* ---------- interactions (delegated: survives facet re-renders) ---------- */

  document.addEventListener('change', function (event) {
    var box = event.target && event.target.closest ? event.target.closest(BOX_SEL) : null;
    if (!box) return;
    var control = box.closest(CONTROL_SEL);
    var table = control && tableFor(control);
    if (!control || !table) return;
    var hidden = hiddenFromBoxes(control);
    writeHidden(storageKey(control.getAttribute('data-tcv-collection')), hidden);
    apply(control, table, hidden);
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var reset = target.closest('[data-tcv-columns-reset]');
    if (reset) {
      var control = reset.closest(CONTROL_SEL);
      var table = control && tableFor(control);
      if (control && table) {
        writeHidden(storageKey(control.getAttribute('data-tcv-collection')), []);
        apply(control, table, []);
      }
      return;
    }

    var sliderButton = target.closest('[data-tcv-slider-prev], [data-tcv-slider-next]');
    if (sliderButton) {
      /* T1F-P7.3 — the colour strip now lives in its own specs-area row
         (.rf-tcv__strip-row) instead of inside the product cell, so the slider
         is resolved from that row as well. Fallback keeps older markup working. */
      var row = sliderButton.closest('.rf-tcv__row, .rf-tcv__strip-row') || sliderButton.closest('tr');
      var slider = row && row.querySelector(SLIDER_SEL);
      var viewport = slider && slider.querySelector('[data-tcv-samples]');
      if (viewport) {
        var direction = sliderButton.hasAttribute('data-tcv-slider-prev') ? -1 : 1;
        viewport.scrollBy({ left: direction * Math.max(viewport.clientWidth * 0.8, 180), behavior: 'smooth' });
      }
      return;
    }

    /* Predictable closing: a click outside an open Columns panel closes it. */
    toArray(document.querySelectorAll(CONTROL_SEL + '[open]')).forEach(function (details) {
      if (!details.contains(target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    toArray(document.querySelectorAll(CONTROL_SEL + '[open]')).forEach(function (details) {
      details.removeAttribute('open');
      var summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* Facets replace the product grid HTML: re-apply saved state afterwards. */
  var pending = null;
  function scheduleSync() {
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(function () {
      pending = null;
      syncAll();
    }, CHANGE_DEBOUNCE);
  }

  window.addEventListener('storage', function (event) {
    if (event.key && event.key.indexOf(STORAGE_PREFIX) === 0) syncAll();
  });

  document.addEventListener('scroll', function (event) {
    var viewport = event.target && event.target.closest ? event.target.closest('[data-tcv-samples]') : null;
    var slider = viewport && viewport.closest(SLIDER_SEL);
    if (slider) syncSlider(slider);
  }, true);

  window.addEventListener('resize', scheduleSync);

  function start() {
    syncAll();
    if (typeof window.MutationObserver === 'function' && document.body) {
      new window.MutationObserver(scheduleSync).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
